import { memo, useCallback, useMemo } from "react";

import { BlankContentForList } from "@/components/features/_components";
import { IconButtonList } from "@/components/features/items/edit/_shared/ui/components/system/list/list-item/list-icon-buttons";

import { Label } from "@/components/ui/label/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";

export type RowData = {
	id: string;
	discountName: string;
	discountCode: string;
	priceSetting: string;
	discountTotal: string;
	inSale: boolean;
};

type Props = {
	id: string;
	title: string;
	rows: Array<RowData>;

	onEditDiscount: (discountID: string) => void;
	onDuplicateDiscount: (duplicateDiscountID: string) => void;
	onRemoveDiscount: (discountID: string) => void;
};

export const DiscountTableRoot = memo(
	({ id, title, rows, onEditDiscount, onDuplicateDiscount, onRemoveDiscount }: Props) => {
		/**
		 * EditIcon 押下時の処理
		 */
		const onClickEdit = useCallback(
			(discountID: string) => () => {
				onEditDiscount(discountID);
			},
			[onEditDiscount],
		);
		/**
		 * CopyIcon 押下時の処理
		 */
		const onClickCopy = useCallback(
			(duplicateDiscountID: string) => () => {
				onDuplicateDiscount(duplicateDiscountID);
			},
			[onDuplicateDiscount],
		);
		/**
		 * DeleteIcon 押下時の処理
		 */
		const onClickDelete = useCallback(
			(discountID: string) => () => {
				onRemoveDiscount(discountID);
			},
			[onRemoveDiscount],
		);

		const handleClickTableRow = useCallback(
			(discountID: string) => () => {
				onEditDiscount(discountID);
			},
			[onEditDiscount],
		);

		const canShowList = useMemo(() => rows.length > 0, [rows]);

		return (
			<div className="space-y-3">
				<h4 className="text-lg font-semibold">{title}</h4>

				{canShowList ? (
					<Table id={id} className="w-full min-w-[max-content]">
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead justifyStyle="center">ディスカウント名</TableHead>

								<TableHead justifyStyle="center" className="w-28">
									ステータス
								</TableHead>

								<TableHead justifyStyle="center" className="w-44">
									ディスカウントコード
								</TableHead>

								<TableHead justifyStyle="center">料金設定</TableHead>

								<TableHead justifyStyle="center">ディスカウント総額</TableHead>

								<TableHead justifyStyle="center" colSpan={2} />
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((row, index) => {
								const discountID = row.id;

								return (
									<TableRow
										key={`${id}-${index}`}
										className="hover:cursor-pointer"
										onClick={handleClickTableRow(discountID)}
									>
										{/* ディスカウント名 */}
										<TableCell className="max-w-40">
											<a
												onClick={onClickEdit(discountID)}
												className="cursor-pointer underline-offset-4 hover:underline"
											>
												{row.discountName}
											</a>
										</TableCell>

										{/* ステータス */}
										<TableCell className="w-28 text-center">
											<Label
												variant={row.inSale ? "green" : "pink"}
												text={row.inSale ? "公開" : "非公開"}
											/>
										</TableCell>

										{/* ディスカウントコード */}
										<TableCell className="w-40 max-w-40 text-center">
											<p className="line-clamp-1">{row.discountCode}</p>
										</TableCell>

										{/* 料金設定 */}
										<TableCell className="max-w-40 text-left">
											<p className="whitespace-pre-wrap">{row.priceSetting}</p>
										</TableCell>

										{/* ディスカウント総額 */}
										<TableCell className="max-w-40 text-center">
											<p>{row.discountTotal.toLocaleString()}</p>
										</TableCell>

										{/* アクション */}
										<TableCell>
											<IconButtonList
												onClickEditButton={onClickEdit(discountID)}
												onClickCopyButton={onClickCopy(discountID)}
												onClickDeleteButton={onClickDelete(discountID)}
											/>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				) : (
					<BlankContentForList content={`${title}はありません。`} />
				)}
			</div>
		);
	},
);
